import { useState } from "react";
import { Search } from "lucide-react";
import { API_BASE } from "../lib/apiBase";

const STEPS = ["consolidation_china", "in_transit_china", "customs_awaiting", "customs_cleared", "received_noida"];

const inputCls = "mt-2 w-full bg-transparent border border-white/20 px-3 py-2.5 font-mono text-[13px] text-white placeholder:text-white/25 focus:outline-none focus:border-[#F59E0B] [color-scheme:dark]";

function fmt(iso) {
        if (!iso) return "—";
        return new Date(iso.slice(0, 10) + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function updatedAgo(iso) {
        if (!iso) return "";
        const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
        return days <= 0 ? "Updated today" : days === 1 ? "Updated yesterday" : `Updated ${days} days ago`;
}

function Stepper({ status, meta }) {
        const at = STEPS.indexOf(status);
        return (
                <ol className="mt-5 space-y-3" data-testid="batch-lookup-steps">
                        {STEPS.map((s, i) => {
                                const done = i <= at;
                                return (
                                        <li key={s} data-testid={`batch-lookup-step-${s}`} data-state={i === at ? "current" : done ? "done" : "todo"}
                                                className={`flex items-start gap-3 text-[13px] ${i === at ? "text-white" : done ? "text-white/60" : "text-white/30"}`}>
                                                <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full border"
                                                        style={{ backgroundColor: done ? meta[s]?.dot : "transparent", borderColor: done ? meta[s]?.dot : "rgba(255,255,255,0.25)" }} />
                                                <span className={i === at ? "font-semibold" : ""}>{meta[s]?.label || s}</span>
                                        </li>
                                );
                        })}
                </ol>
        );
}

function Result({ res, meta }) {
        if (!res.batch) {
                return (
                        <p data-testid="batch-lookup-no-batch" className="mt-6 text-white/70 text-sm max-w-[60ch]">
                                {res.order_name ? `Order ${res.order_name} (placed ${fmt(res.ordered_on)})` : `Orders placed on ${fmt(res.ordered_on)}`} haven&rsquo;t
                                been assigned to a batch yet. Batches are added once they close, so check back in a few days or message us on WhatsApp.
                        </p>
                );
        }
        const b = res.batch;
        return (
                <div data-testid="batch-lookup-result" className="mt-6 border border-[#F59E0B]/40 bg-[#F59E0B]/[0.05] p-5 max-w-[640px]">
                        <p className="font-mono text-[11px] tracking-[0.16em] uppercase text-white/50">
                                {res.order_name ? `Order ${res.order_name} · placed ${fmt(res.ordered_on)}` : `Orders placed ${fmt(res.ordered_on)}`}
                        </p>
                        <p data-testid="batch-lookup-name" className="mt-2 font-display uppercase text-[22px] text-white leading-tight">{b.batch_name}</p>
                        <p className="mt-1 font-mono text-[11px] text-white/50">
                                Covers orders {fmt(b.orders_from)} → {fmt(b.orders_to)} · <span data-testid="batch-lookup-updated">{updatedAgo(b.updated_at)}</span>
                        </p>
                        <Stepper status={b.status} meta={meta} />
                </div>
        );
}

export function BatchLookup({ meta }) {
        const [mode, setMode] = useState("order");
        const [order, setOrder] = useState("");
        const [email, setEmail] = useState("");
        const [date, setDate] = useState("");
        const [busy, setBusy] = useState(false);
        const [err, setErr] = useState("");
        const [res, setRes] = useState(null);

        const submit = async (e) => {
                e.preventDefault();
                setErr(""); setRes(null);
                if (mode === "order" && (!order.trim() || !email.includes("@"))) { setErr("Enter your order number and the email you used at checkout."); return; }
                if (mode === "date" && !date) { setErr("Pick the date you placed your order."); return; }
                setBusy(true);
                try {
                        const r = mode === "order"
                                ? await fetch(`${API_BASE}/api/preorder-batches/lookup`, {
                                        method: "POST", headers: { "Content-Type": "application/json" },
                                        body: JSON.stringify({ order_number: order, email }),
                                })
                                : await fetch(`${API_BASE}/api/preorder-batches/for-date?date=${date}`);
                        const j = await r.json().catch(() => ({}));
                        if (r.status === 404) { setErr("We couldn't find a pre-order with that order number and email. Older orders may not be on record here, so try searching by order date instead."); return; }
                        if (r.status === 429) { setErr("Too many searches. Please wait a few minutes and try again."); return; }
                        if (!r.ok) { setErr("Something went wrong. Please try again shortly."); return; }
                        setRes(mode === "order" ? j : { ...j, ordered_on: date });
                } catch { setErr("Network error. Please try again."); } finally { setBusy(false); }
        };

        const tab = (m, label) => (
                <button type="button" data-testid={`batch-lookup-tab-${m}`} onClick={() => { setMode(m); setErr(""); setRes(null); }}
                        className={`font-mono text-[11px] tracking-[0.16em] uppercase px-4 py-2 border transition-colors ${mode === m ? "border-[#F59E0B] bg-[#F59E0B] text-black" : "border-white/20 text-white/60 hover:text-white"}`}>
                        {label}
                </button>
        );

        return (
                <section className="mt-12" data-testid="batch-lookup">
                        <p className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#F59E0B]">Find my pre-order</p>
                        <h2 className="mt-2 font-display uppercase text-[22px] md:text-[28px] text-white leading-tight">Where is my batch?</h2>
                        <div className="mt-5 flex gap-2">{tab("order", "Order number")}{tab("date", "Order date")}</div>
                        <form onSubmit={submit} className="mt-5 grid gap-4 md:grid-cols-[1fr_1fr_auto] items-end max-w-[760px]" data-testid="batch-lookup-form">
                                {mode === "order" ? (
                                        <>
                                                <label className="font-mono text-[11px] tracking-[0.16em] uppercase text-white/60">Order number
                                                        <input data-testid="batch-lookup-order" value={order} onChange={(e) => setOrder(e.target.value)} placeholder="#23064" className={inputCls} />
                                                </label>
                                                <label className="font-mono text-[11px] tracking-[0.16em] uppercase text-white/60">Checkout email
                                                        <input data-testid="batch-lookup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className={inputCls} />
                                                </label>
                                        </>
                                ) : (
                                        <label className="font-mono text-[11px] tracking-[0.16em] uppercase text-white/60 md:col-span-2">Date you placed the order
                                                <input data-testid="batch-lookup-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
                                        </label>
                                )}
                                <button data-testid="batch-lookup-submit" type="submit" disabled={busy}
                                        className="inline-flex items-center justify-center gap-2 bg-[#F59E0B] text-black font-mono text-[12px] tracking-[0.18em] uppercase px-5 py-3 hover:bg-[#ffb437] disabled:opacity-50">
                                        <Search className="h-4 w-4" /> {busy ? "Searching…" : "Find batch"}
                                </button>
                        </form>
                        {err && <p data-testid="batch-lookup-error" className="mt-4 text-red-400/90 text-sm max-w-[68ch]">{err}</p>}
                        {res && <Result res={res} meta={meta} />}
                </section>
        );
}
